/* eslint-disable no-underscore-dangle */

/**
 * Register all available CharJS charts.
 *
 * We will modify the draw function of the DoughnutChart to
 *   draw rounded corners for each visible arc.
 */
import style from '../chart.scss';

export default {
	name: 'Chart',

	props: {
		chartData: {
			type: Object,
			required: true,
			description: 'Sets the data for the data in the chart',
		},

		chartHeight: {
			type: String,
			required: false,
			default: '400px',
			validator: height => height.indexOf('px') > -1 || height.indexOf('%') > -1,
			description: 'Sets the height of the graph in px or %',
		},

		chartWidth: {
			type: String,
			required: false,
			default: '100%',
			validator: width => width.indexOf('px') > -1 || width.indexOf('%') > -1,
			description: 'Sets the width of the graph in px or %',
		},

		defaultColors: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets an array of colors that will be used for the data if they are not passed a specific color',
		},

		options: {
			type: Object,
			required: false,
			default: () => ({}),
			description: 'Sets a plethora of ChartJS options for the graph, may override some default options, see ChartJS documentation that match the ChartJS version used in Helio',
		},

		plugins: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets the ChartJS plugins, see ChartJS documentation that match the ChartJS version used in Helio',
		},

		theme: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'light',
				'dark',
			].indexOf(value) !== -1,
			description: 'Sets the dark/light theme of the graph',
		},

		tooltipHasColor: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether or not there is a colored dot on the tooltip matching the data color',
		},

		tooltipHasLabel: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether or not the name of the data appears on its tooltip',
		},

		tooltipUnitPrefix: {
			type: String,
			required: false,
			default: '',
			description: 'Sets a prefix before the tooltip value, ex. $12',
		},

		tooltipUnitSuffix: {
			type: String,
			required: false,
			default: '',
			description: 'Sets a suffix after the tooltip value, ex. 85%',
		},

		useLongDescription: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether an autogenerated description of the data in a Cartesian chart will be used. May be turned off if a project provides a text alternative in the context of this component\'s use.',
		},
	},

	data() {
		return {
			_chart: null,
			_plugins: this.plugins,
			chartId: '',
			chartType: '',
			tooltipHtml: '',
			tooltipAnchorX: null,
			tooltipAnchorY: null,
			chartArea: null,
		};
	},

	computed: {
		renderChartOptions() {
			if (this.style) {
				return {
					responsive: true,
					maintainAspectRatio: false,
					...this.chartOptions,
					...this.options,
				};
			}

			return {};
		},

		dataOptions() {
			return {};
		},

		data() {
			const dataset = this.chartData.datasets.reduce(
				(acc, curr, index) => {
					acc.data.push(curr.data || 0);
					acc.backgroundColor.push(curr.backgroundColor
						|| this.defaultChartColors[index % this.defaultChartColors.length]);

					return acc;
				}, {
					data: [],
					backgroundColor: [],
					barPercentage: 1,
					borderWidth: 0,
					...this.dataOptions,
				},
			);

			return {
				datasets: [dataset],
				labels: this.chartData.datasets.map(item => item.datasetLabel || ''),
			};
		},

		style() {
			const styleObject = {};

			Object.keys(style).forEach(key => {
				if (key.indexOf('-default-chart-colors') > -1) {
					styleObject[key] = style[key].split(', ');
				} else {
					styleObject[key] = style[key];
				}
			});

			return styleObject;
		},

		defaultChartColors() {
			return this.defaultColors && this.defaultColors.length
				? this.defaultColors
				: this.style[`${this.theme}-default-chart-colors`];
		},
	},

	mounted() {
		this.addPlugins([
			...this.plugins,
		]);

		this.renderChart(this.data, this.renderChartOptions);
	},

	beforeDestroy() {
		if (this.$data._chart) {
			this.$data._chart.destroy();
		}
	},

	watch: {
		theme() {
			this.renderChart(this.data, this.renderChartOptions);
		},

		// eslint-disable-next-line func-names
		'$i18n.locale': function () {
			this.renderChart(this.data, this.chartOptions);
		},
	},

	methods: {
		addPlugins(plugin) {
			this.$data._plugins.push(...plugin);
		},

		renderChart(data, options) {
			if (this.$data._chart) this.$data._chart.destroy();
			if (!this.$refs.canvas) throw new Error('Please remove the <template></template> tags from your chart component. See https://vue-chartjs.org/guide/#vue-single-file-components');
			this.$data._chart = this.$chartjs(
				this.$refs.canvas.getContext('2d'), {
					type: this.chartType,
					data,
					options,
					plugins: this.$data._plugins,
				},
			);
			this.chartArea = this.$data._chart.chartArea;
		},
	},
};
